import * as React from 'react';
import {
  Box,
  Button1,
  Color,
  Display,
  ResponsiveValue,
  TextAlign,
  Theme,
  withCitrusTheme,
} from '@gousto-internal/citrus-react';
import Link, { LinkProps } from 'next/link';
import { InternalLink } from '../InternalLink/InternalLink';

export enum ButtonLinkColorVariant {
  Primary = 'Primary',
  Secondary = 'Secondary',
}

function colorVariantStyle(theme: Theme, colorVariant: ButtonLinkColorVariant) {
  if (colorVariant === ButtonLinkColorVariant.Secondary) {
    return {
      height: '40px',
      backgroundColor: theme.color(Color.White),
      color: theme.color(Color.Secondary_400),
      boxShadow: `inset 0 0 0 1px ${theme.color(Color.Secondary_400)}`,
      fontWeight: 400,
      '&:hover,&:focus': {
        backgroundColor: theme.color(Color.Secondary_50),
        color: theme.color(Color.Secondary_600),
        boxShadow: `inset 0 0 0 1px ${theme.color(Color.Secondary_400)}`,
      },
      '&:focus-visible': {
        boxShadow: `inset 0 0 0 3px ${theme.color(Color.Secondary_600)}`,
      },
    };
  }

  return {
    backgroundColor: theme.color(Color.Secondary_400),
    color: theme.color(Color.White),
    '&:hover,&:focus': {
      backgroundColor: theme.color(Color.Secondary_600),
    },
    '&:focus': {
      outline: `2px solid ${theme.color(Color.Secondary_600)}`,
      boxShadow: `inset 0 0 0 1px ${theme.color(Color.White)}`,
    },
  };
}

function buttonLinkStyle(theme: Theme, colorVariant: ButtonLinkColorVariant) {
  return [
    {
      textDecoration: 'none',
      width: '100%',
      ...colorVariantStyle(theme, colorVariant),
    },
    theme.radius(1.5),
    theme.responsiveSpacing([3, null, 2], ['paddingTop', 'paddingBottom']),
    theme.responsiveSpacing(6, ['paddingLeft', 'paddingRight']),
  ];
}

const styledRouterLinkByVariant = {
  [ButtonLinkColorVariant.Primary]: withCitrusTheme(Link, (theme) =>
    buttonLinkStyle(theme, ButtonLinkColorVariant.Primary),
  ),
  [ButtonLinkColorVariant.Secondary]: withCitrusTheme(Link, (theme) =>
    buttonLinkStyle(theme, ButtonLinkColorVariant.Secondary),
  ),
};

type ButtonLinkProps = {
  width?: ResponsiveValue<string>;
  colorVariant?: ButtonLinkColorVariant;
  children: React.ReactNode;
} & LinkProps;

/**
 * `next/link` styled as Citrus `Button` for improved accessibility
 *
 * Accepts all `next/link` props
 */
export function ButtonLink(props: ButtonLinkProps) {
  const { children, width, colorVariant = ButtonLinkColorVariant.Primary, ...linkProps } = props;
  const StyledRouterLink = styledRouterLinkByVariant[colorVariant];

  return (
    <Box width={width} display={Display.Flex}>
      <InternalLink href={linkProps.href as string}>
        <StyledRouterLink {...linkProps}>
          <Button1 textAlign={TextAlign.Center}>{children}</Button1>
        </StyledRouterLink>
      </InternalLink>
    </Box>
  );
}

export const IconButtonLink = withCitrusTheme(ButtonLink, (theme) =>
  theme.responsiveSpacing(2, ['padding']),
);

const styledAnchorByVariant = {
  [ButtonLinkColorVariant.Primary]: withCitrusTheme('a', (theme) =>
    buttonLinkStyle(theme, ButtonLinkColorVariant.Primary),
  ),
  [ButtonLinkColorVariant.Secondary]: withCitrusTheme('a', (theme) =>
    buttonLinkStyle(theme, ButtonLinkColorVariant.Secondary),
  ),
};

type ExternalButtonLinkProps = {
  width?: ResponsiveValue<string>;
  colorVariant?: ButtonLinkColorVariant;
  children: React.ReactNode;
} & React.AnchorHTMLAttributes<HTMLAnchorElement>;

/**
 * Real `<a>` styled as Citrus `Button`, for external links (new tab, etc.)
 * where `next/link` client-side routing doesn't apply.
 */
export function ExternalButtonLink(props: ExternalButtonLinkProps) {
  const { children, width, colorVariant = ButtonLinkColorVariant.Primary, ...anchorProps } = props;
  const StyledAnchor = styledAnchorByVariant[colorVariant];

  return (
    <Box width={width} display={Display.Flex}>
      <StyledAnchor {...anchorProps}>
        <Button1 textAlign={TextAlign.Center}>{children}</Button1>
      </StyledAnchor>
    </Box>
  );
}
